import { Divider, Card } from 'antd'
import { FC, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { PerAtAGlance } from '../../../../components/custom_components/AtAGlance/AtAGlance'
import { PerAtAGlanceElement } from '../../../../components/custom_components/AtAGlance/AtAGlanceElement'
import { PerCardInfo } from '../../../../components/custom_components/CardInfo'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { FullMeetingFragment, MeetingStepStatus, MeetingStepViewId, Employee, RoleName, SkillEmployeeStatusDto } from '../../../../types'
import { MeetingsHistoryTableContainer } from '../../../meeting/shared/MeetingsHistoryTableContainer'
import { AppraisalStepBtns } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { AppraisalWelcomeTeaser } from '../shared/AppraisalWelcomeTeaser'
import { GecoAtAGlance } from '../shared/Geco/GecoAtAGlance'
import GeneralFeedback from '../shared/GeneralFeedback'
import { isAppraisalUpdate } from '../meeting/util/meetingUtil'
import PerText from '../../../../components/custom_components/Text'
import { getEmployeeFirstName } from '../../../../shared/employeeUtil'
import { useUserManagersRolesByCompany } from '../../../../graphql/company/hooks/useUserManagerRolesByCompany'
import { refreshEmployeeSkillStatusInMeeting } from '../../../../graphql/skill-status/useSkillStatusMutations'
import { useEmployeeSkillStatusInMeeting } from '../../../../graphql/skill-status/useSkillStatusQueries'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import { SkillStatusLabelComponent, SkillStatusDescription } from '../../../skillStatus/SkillStatusLabelComponent'
import { KudosCarousel } from '../../../../components/custom_components/Carousel/KudosCarousel'
import { useAuth } from '../../../../context/auth-context'
import PerButtonCircle from '../../../../components/custom_components/ButtonCircle'
import { computeFullname } from '../../../../shared/nameDisplayPreferencesUtil'
import { CreateKudosModalInOverviewStep } from '../../../../components/custom_components/CreateKudosModal/CreateKudosModalInOverviewStep'
import { useKudosForEmployeeInOverviewStep } from '../../../../graphql/kudos/useKudosForEmployeeWithCache'
import useIsFeatureEnabled from '../../../../graphql/featureToggle/useIsFeatureEnabled'
import { FeatureNameEnum } from '../../../../graphql/featureToggle/FeatureNameEnum'
import { EmployeeActivityListComponent } from '../../../badge/EmployeeActivityListComponent'

interface Props extends AppraisalStepBtns {
  meeting: FullMeetingFragment
}

const ManagerMeetingStepOverview: FC<Props> = ({ meeting, nextBtn }) => {
  const [t] = useTranslation()
  const [isRefreshSkillStatusProcessing, toggleRefreshSkillStatusProcessing] = useState<boolean>(false)
  const [skillStatusError, setSkillStatusError] = useState<boolean>(false)
  const [isCreateKudosModalVisible, setIsCreateKudosModalVisible] = useState<boolean>(false)
  const { isFeatureEnabled: isKudosEnvVarEnabled } = useIsFeatureEnabled(FeatureNameEnum.KUDOS)

  const { data, loading, error } = useEmployeeSkillStatusInMeeting(meeting.employee.id)
  const { data: userRolesData } = useUserManagersRolesByCompany(meeting.appraisalSeason!.companyId)
  const { user } = useAuth()

  const {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: kudosLoading,
    error: kudosError,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
    onCreateKudos,
  } = useKudosForEmployeeInOverviewStep(meeting.employee.id, meeting.type, meeting.appraisalSeason.year)

  if (!data || loading || error) {
    return <LoadingAndErrors hasNoData={!data} isLoading={loading} error={error} />
  }
  const skillStatus: SkillEmployeeStatusDto | null | undefined = data.getEmployeeSkillStatusInMeeting

  const userRoles: RoleName[] =
    userRolesData && userRolesData.findUserManagerRolesByCompany ? userRolesData.findUserManagerRolesByCompany : []
  const isBuOrCompanyManager = userRoles.includes(RoleName.BuManager) || userRoles.includes(RoleName.CompanyManager)

  return (
    <div data-e2e-manager-meetings-step-overview>
      <MeetingBackground>
        <AppraisalWelcomeTeaser
          subtitle={t('appraisalMeeting.stepOverview.pageDescriptionSubtitleManager')}
          info={t('appraisalMeeting.stepOverview.pageDescriptionManager')}
        />

        <PerCardInfo
          heading={t('appraisalMeeting.stepOverview.infoCard.heading')}
          description={t('appraisalMeeting.stepOverview.infoCard.description')}
        />
        <Divider />

        <PerText appearance="tile-heading" mb="s">
          {t('appraisalMeeting.stepOverview.skillCenterManager.header')}
        </PerText>

        <Card bordered={false}>
          <PerFlexRow align="middle">
            {isBuOrCompanyManager && (
              <PerText mb="xxs">
                <Trans
                  i18nKey="appraisalMeeting.stepOverview.skillCenterManager.desc"
                  values={{
                    employee: getEmployeeFirstName(meeting.employee as Employee),
                  }}
                  components={[
                    <a
                      style={{ textDecoration: 'underline', textTransform: 'uppercase', fontWeight: 'bold' }}
                      href={`${meeting.employee.skillCenterUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {''}
                    </a>,
                  ]}
                />
              </PerText>
            )}
          </PerFlexRow>
          <SkillStatusLabelComponent
            skillStatus={skillStatus}
            isProcessing={isRefreshSkillStatusProcessing}
            skillStatusErrorVisible={skillStatusError}
          />
        </Card>

        <SkillStatusDescription
          skillStatus={skillStatus}
          skillStatusErrorVisible={skillStatusError}
          onClick={async () => {
            toggleRefreshSkillStatusProcessing(true)
            const { data: skillStatusData, errors: skillStatusErrors } = await refreshEmployeeSkillStatusInMeeting(meeting.employee.id)
            if (!skillStatusData && skillStatusErrors) {
              setSkillStatusError(true)
            }
            toggleRefreshSkillStatusProcessing(false)
          }}
          isProcessing={isRefreshSkillStatusProcessing}
        />

        {meeting.activityListEnabled && (
          <>
            <Divider />
            <EmployeeActivityListComponent
              employeeId={meeting.employee.id}
              title={t('appraisalMeeting.stepOverview.activityList.header')}
              lastYear
            />
          </>
        )}

        {isKudosEnvVarEnabled && isKudosEnabled && (
          <>
            <Divider />
            <PerText appearance="tile-heading" mb="xs">
              {t('kudos.header')}
            </PerText>

            {!employeeOptedOut && (
              <PerFlexRow justify="space-between" align="middle" mb="xs">
                <PerText color="secondary">
                  <Trans
                    i18nKey={meeting.type === 'AppraisalUpdate' ? 'kudos.appraisalUpdate.complete' : 'kudos.appraisal.complete'}
                    values={{
                      year: meeting.appraisalSeason.year,
                      currentYear: meeting.appraisalSeason.year,
                      previousYear: meeting.appraisalSeason.year - 1,
                      employeeName: getEmployeeFirstName(meeting.employee as Employee),
                    }}
                    components={[
                      <a
                        key="profile-link"
                        href={`/profile/${meeting.employee.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#05cbce', textDecoration: 'underline' }}
                      >
                        {getEmployeeFirstName(meeting.employee as Employee)}
                      </a>,
                    ]}
                  />
                </PerText>
                <PerButtonCircle
                  icon="add_stroke"
                  color="primary"
                  variant="pastel"
                  onClick={() => setIsCreateKudosModalVisible(true)}
                  tooltip={t('kudos.createModal.title')}
                />
              </PerFlexRow>
            )}

            <KudosCarousel
              privateProfileView={false}
              employeeOptedOut={employeeOptedOut}
              kudos={currentKudos}
              nameDisplayPreference={user.nameDisplayPreference}
              loading={kudosLoading}
              error={kudosError || null}
              currentPage={currentPage}
              totalPages={totalPages}
              onNextPage={goToNextPage}
              onPreviousPage={goToPreviousPage}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
            />
          </>
        )}

        <Divider />

        <PerAtAGlance>
          <PerAtAGlanceElement
            icon="project_stroke"
            heading={t('appraisalMeeting.stepOverview.pageDescriptionProjects.titleGecoInfo')}
            info={t('appraisalMeeting.stepOverview.pageDescriptionProjects.subtitleGecoInfo')}
          >
            <GecoAtAGlance
              employeeId={meeting.employee.id}
              projectDisplayLimit={3}
              as={meeting.appraisalSeason}
              appraisalUpdate={isAppraisalUpdate(meeting)}
            />
          </PerAtAGlanceElement>
        </PerAtAGlance>

        <GeneralFeedback meeting={meeting} />
        <Divider />

        <MeetingsHistoryTableContainer
          employeeId={meeting.employee.id}
          employeeFirstName={meeting.employee.chosenFirstName ? meeting.employee.chosenFirstName : meeting.employee.legalFirstName}
          showReviewer
          emptyTextType="meetingReviewer"
        />
      </MeetingBackground>

      <PerFlexRow justify="space-between">
        <div>&nbsp;</div>
        {nextBtn && nextBtn(MeetingStepStatus.Done)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Intro} stepStatus={MeetingStepStatus.Done} />

      <CreateKudosModalInOverviewStep
        visible={isCreateKudosModalVisible}
        closeModal={() => setIsCreateKudosModalVisible(false)}
        employeeId={meeting.employee.id}
        employeeFullName={computeFullname(user.nameDisplayPreference, meeting.employee.legalFirstName, meeting.employee.legalLastName)}
        meetingType={meeting.type}
        appraisalSeasonYear={meeting.appraisalSeason.year}
        onKudosCreated={onCreateKudos}
      />
    </div>
  )
}
export default ManagerMeetingStepOverview
